import dayjs from 'dayjs';
import pluralize from 'pluralize';

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
const URGENT_THRESHOLD_MINUTES = MINUTES_PER_DAY;

export const TIME_LEFT_URGENT_ICON_COLORS = {
  backgroundColor: '#FFC6CA',
  strokeColor: '#FF6D79',
} as const;

export const TIME_LEFT_DEFAULT_ICON_COLORS = {
  backgroundColor: '#D9D0FF',
  strokeColor: '#5b98d1',
} as const;

export interface TimeLeftData {
  remainingMinutes: number;
  time: string;
  unit: string;
  compactLabel: string;
  isUrgent: boolean;
}

export const parseCompactTimeLeftLabel = (label: string): number | null => {
  const match = /^(\d+)([dhm])$/i.exec(label.trim());

  if (!match) {
    return null;
  }

  const value = Number(match[1]);
  const suffix = match[2].toLowerCase();

  if (suffix === 'd') {
    return value * MINUTES_PER_DAY;
  }

  if (suffix === 'h') {
    return value * MINUTES_PER_HOUR;
  }

  return value;
};

export const getRemainingMinutesFromExpiresOn = (expiresOn: string | null | undefined): number | null => {
  if (!expiresOn) {
    return null;
  }

  const expiry = dayjs(expiresOn);

  if (!expiry.isValid() || !expiry.isAfter(dayjs())) {
    return null;
  }

  return expiry.diff(dayjs(), 'minute');
};

export const getRemainingMinutesFromHours = (timeLimitHours: number | null | undefined): number | null => {
  if (timeLimitHours == null || timeLimitHours <= 0) {
    return null;
  }

  return Math.floor(timeLimitHours * MINUTES_PER_HOUR);
};

export const formatTimeLeftData = (remainingMinutes: number): TimeLeftData | null => {
  if (remainingMinutes <= 0) {
    return null;
  }

  if (remainingMinutes >= URGENT_THRESHOLD_MINUTES) {
    const days = Math.floor(remainingMinutes / MINUTES_PER_DAY);

    return {
      remainingMinutes,
      time: String(days),
      unit: pluralize('day', days),
      compactLabel: `${days}d`,
      isUrgent: false,
    };
  }

  if (remainingMinutes >= MINUTES_PER_HOUR) {
    const hours = Math.floor(remainingMinutes / MINUTES_PER_HOUR);

    return {
      remainingMinutes,
      time: String(hours),
      unit: pluralize('hour', hours),
      compactLabel: `${hours}h`,
      isUrgent: true,
    };
  }

  const minutes = Math.max(remainingMinutes, 1);

  return {
    remainingMinutes,
    time: String(minutes),
    unit: 'min',
    compactLabel: `${minutes}m`,
    isUrgent: true,
  };
};

export const resolveTimeLeftData = (input: {
  remainingMinutes?: number | null;
  timeLimitHours?: number | null;
  expiresOn?: string | null;
  compactLabel?: string | null;
}): TimeLeftData | null => {
  let remainingMinutes = input.remainingMinutes ?? null;

  if (remainingMinutes == null && input.expiresOn) {
    remainingMinutes = getRemainingMinutesFromExpiresOn(input.expiresOn);
  }

  if (remainingMinutes == null && input.timeLimitHours != null) {
    remainingMinutes = getRemainingMinutesFromHours(input.timeLimitHours);
  }

  if (remainingMinutes == null && input.compactLabel) {
    remainingMinutes = parseCompactTimeLeftLabel(input.compactLabel);
  }

  if (remainingMinutes == null) {
    return null;
  }

  return formatTimeLeftData(remainingMinutes);
};
